import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, LogOut, Film, Trash2, HardDrive, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Logo } from "@/components/auth/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, formatBytes } from "@/lib/api";

const HERO = "https://images.pexels.com/photos/29043652/pexels-photo-29043652.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1400";

const ProjectCard = ({ p, onOpen, onDelete, i }) => {
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="group bg-[#181A22] border border-[#2A2E3D] rounded-lg p-5 hover:border-[#FFB800]/60 transition-colors fade-up cursor-pointer"
      style={{ animationDelay: `${i * 60}ms` }} onClick={onOpen} data-testid={`project-card-${p.id}`}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-md bg-[#0B0C10] border border-[#2A2E3D] flex items-center justify-center"><Film className="w-4 h-4 text-[#FFB800]" /></div>
        <button onClick={(e) => { e.stopPropagation(); confirm ? onDelete() : setConfirm(true); }} onMouseLeave={() => setConfirm(false)}
          className={`text-xs px-2 py-1 rounded transition-colors ${confirm ? "bg-red-500/20 text-red-300" : "text-slate-500 opacity-0 group-hover:opacity-100 hover:text-red-400"}`}
          data-testid={`delete-project-${p.id}`}>
          {confirm ? "Confirm delete" : <Trash2 className="w-4 h-4" />}
        </button>
      </div>
      <h3 className="font-display text-lg font-semibold mt-4 truncate">{p.name}</h3>
      <p className="text-xs text-slate-400 mt-1 line-clamp-2 min-h-[2rem]">{p.description || "No description"}</p>
      <div className="flex gap-4 mt-4 font-mono-ui text-[11px] text-slate-500">
        <span>{p.asset_count} assets</span><span className="flex items-center gap-1"><HardDrive className="w-3 h-3" />{formatBytes(p.total_size)}</span>
        <span className="ml-auto">{new Date(p.updated_at).toLocaleDateString()}</span>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/projects", form); nav(`/project/${data.id}`); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/projects/${id}`); toast.success("Project deleted"); load(); };

  return (
    <div className="min-h-screen bg-[#0B0C10]">
      <header className="h-14 bg-[#0B0C10]/95 border-b border-[#2A2E3D] sticky top-0 z-50 px-6 flex items-center justify-between backdrop-blur-xl">
        <Logo />
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-400" data-testid="user-name">{user?.name}</span>
          <Button variant="ghost" size="sm" onClick={logout} className="text-slate-300" data-testid="logout-button"><LogOut className="w-4 h-4 mr-2" />Sign out</Button>
        </div>
      </header>
      <section className="relative h-[300px] overflow-hidden grain">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C10] via-[#0B0C10]/80 to-transparent" />
        <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-center fade-up">
          <p className="font-mono-ui text-xs tracking-[0.3em] text-[#FFB800]">PROJECT LIBRARY</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-3 max-w-2xl">What are we cutting today, {user?.name?.split(" ")[0]}?</h1>
          <Button onClick={() => setOpen(true)} className="mt-6 w-fit h-11 px-6 bg-[#FFB800] text-[#0B0C10] hover:bg-[#ffc933] font-semibold" data-testid="new-project-button">
            <Plus className="w-4 h-4 mr-2" />New project
          </Button>
        </div>
      </section>
      <main className="max-w-7xl mx-auto px-6 py-12">
        {projects === null ? <Loader2 className="w-5 h-5 animate-spin text-slate-500" /> : projects.length === 0 ? (
          <div className="border border-dashed border-[#2A2E3D] rounded-lg p-14 text-center text-slate-400" data-testid="empty-projects">
            No projects yet. Start one and upload your first footage (up to 3 GB).
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="projects-grid">
            {projects.map((p, i) => <ProjectCard key={p.id} p={p} i={i} onOpen={() => nav(`/project/${p.id}`)} onDelete={() => remove(p.id)} />)}
          </div>
        )}
      </main>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#13151C] border-[#2A2E3D]" data-testid="new-project-dialog">
          <DialogHeader><DialogTitle className="font-display">New project</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <Input placeholder="Project name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0B0C10]" data-testid="project-name-input" />
            <Textarea placeholder="Logline or notes (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0B0C10]" data-testid="project-description-input" />
            <Button type="submit" disabled={busy} className="w-full bg-[#FFB800] text-[#0B0C10] hover:bg-[#ffc933]" data-testid="create-project-submit">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Create & open editor
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
